/** The loadout editor's pure parts: what the picker is for, where a pick goes, and a fault as a sentence. */
import type { KitContainer, KitWeapon, LoadoutKit, LoadoutProblem } from '../api/catalog';
import { ca } from '../i18n/ca';

const e = ca.loadoutEditor;

export type WeaponSlot = 'primary' | 'launcher' | 'handgun';
export type ContainerSlot = 'uniform' | 'vest' | 'backpack';
export type OneSlot = 'headgear' | 'goggles' | 'binocular';
export type Part = 'optic' | 'muzzle' | 'laser' | 'bipod';

/** What the picker is choosing for; its kinds decide what the mod set offers. */
export type Target =
  | { type: 'one'; slot: OneSlot }
  | { type: 'container'; slot: ContainerSlot }
  | { type: 'contents'; slot: ContainerSlot }
  | { type: 'weapon'; slot: WeaponSlot }
  | { type: 'part'; slot: WeaponSlot; part: Part }
  | { type: 'magazine'; slot: WeaponSlot }
  | { type: 'linked' };

export const targetKey = (t: Target): string => {
  switch (t.type) {
    case 'linked':
      return 'linked';
    case 'part':
      return `part:${t.slot}:${t.part}`;
    default:
      return `${t.type}:${t.slot}`;
  }
};

/** The kinds a target may be given, the first the one shown to begin with. */
export function kindsFor(t: Target): string[] {
  switch (t.type) {
    case 'one':
    case 'container':
    case 'weapon':
      return [t.slot];
    case 'part':
      return [t.part];
    case 'magazine':
      return ['magazine'];
    case 'linked':
      return ['item'];
    case 'contents':
      return ['item', 'magazine'];
  }
}

export const targetLabel = (t: Target): string => {
  switch (t.type) {
    case 'linked':
      return e.slots.linkedItems;
    case 'part':
      return `${e.slots[t.slot]} · ${e.parts[t.part]}`;
    case 'magazine':
      return `${e.slots[t.slot]} · ${e.parts.magazine}`;
    case 'contents':
      return `${e.slots[t.slot]} · ${e.parts.contents}`;
    default:
      return e.slots[t.slot];
  }
};

const blankWeapon = (className: string): KitWeapon => ({
  className,
  muzzle: null,
  laser: null,
  optic: null,
  magazines: [],
  bipod: null,
});

/** Puts a chosen class where the target says, leaving the rest of the kit as it was. */
export function applyPick(kit: LoadoutKit, t: Target, className: string): LoadoutKit {
  switch (t.type) {
    case 'one':
      return { ...kit, [t.slot]: className };
    case 'container':
      return { ...kit, [t.slot]: { className, contents: kit[t.slot]?.contents ?? [] } };
    case 'contents': {
      const box: KitContainer = kit[t.slot] ?? { className: '', contents: [] };
      const have = box.contents.find((i) => i.className === className);
      const contents = have
        ? box.contents.map((i) => (i === have ? { ...i, count: Math.min(99, i.count + 1) } : i))
        : [...box.contents, { className, count: 1 }];
      return { ...kit, [t.slot]: { ...box, contents } };
    }
    case 'weapon':
      return { ...kit, [t.slot]: { ...(kit[t.slot] ?? blankWeapon(className)), className } };
    case 'part': {
      const w = kit[t.slot];
      return w ? { ...kit, [t.slot]: { ...w, [t.part]: className } } : kit;
    }
    case 'magazine': {
      const w = kit[t.slot];
      if (!w) return kit;
      const have = w.magazines.find((m) => m.className === className);
      if (!have && w.magazines.length >= 2) return kit;
      const magazines = have
        ? w.magazines.map((m) => (m === have ? { ...m, count: Math.min(99, m.count + 1) } : m))
        : [...w.magazines, { className, count: 1 }];
      return { ...kit, [t.slot]: { ...w, magazines } };
    }
    case 'linked':
      return kit.linkedItems.includes(className)
        ? kit
        : { ...kit, linkedItems: [...kit.linkedItems, className] };
  }
}

/** Every class the kit names, for resolving what each is called in one call. */
export function classesOf(kit: LoadoutKit): string[] {
  const out = new Set<string>();
  const add = (c: string | null | undefined) => c && out.add(c);
  for (const w of [kit.primary, kit.launcher, kit.handgun]) {
    if (!w) continue;
    [w.className, w.muzzle, w.laser, w.optic, w.bipod].forEach(add);
    w.magazines.forEach((m) => add(m.className));
  }
  for (const c of [kit.uniform, kit.vest, kit.backpack]) {
    if (!c) continue;
    add(c.className);
    c.contents.forEach((i) => add(i.className));
  }
  [kit.headgear, kit.goggles, kit.binocular, ...kit.linkedItems].forEach(add);
  return [...out];
}

/** One fault of a refused or checked kit as a sentence: which piece, then why. */
export function problemText(p: LoadoutProblem): string {
  const [slot, part] = p.slot.split('.');
  const where = [e.slots[slot] ?? slot, part ? (e.parts[part] ?? part) : null]
    .filter(Boolean)
    .join(' · ');
  const say = e.problem[p.reason];
  const why = say
    ? say(p.className, p.found, e.kindIs[p.found] ?? '')
    : e.problemOther(p.slot, p.className);
  return `${where}: ${why}`;
}
